import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CustomizationWizard } from './occasions/CustomizationWizard';
export function Hero() {
  const navigate = useNavigate();
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  return <>
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-515">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-516">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-517" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-518" />
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -bottom-8 left-[30%] w-72 h-72 bg-yellow-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-519" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-520">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-521">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-522">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-523">
                <Sparkles className="w-4 h-4 text-rose-400 mr-2" data-id="element-524" />
                <span className="text-sm font-medium text-gray-600" data-id="element-525">
                  Handcrafted with love
                </span>
              </div>

              <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6" data-id="element-526">
                Celebrate Every <br data-id="element-527" />
                <span className="text-rose-500" data-id="element-528">Special Moment</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg" data-id="element-529">
                Discover our curated collection of personalized balloons,
                bespoke gift boxes, and fresh flowers designed to make memories
                last forever.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-530">
                <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-532" />} onClick={() => navigate('/shop')} data-id="element-531">
                  Shop Collection
                </Button>
                <Button variant="outline" size="lg" onClick={() => setIsCustomizationOpen(true)} data-id="element-533">
                  Custom Orders
                </Button>
              </div>

              <div className="mt-12 flex items-center gap-8 text-sm text-gray-500" data-id="element-534">
                <div className="flex items-center" data-id="element-535">
                  <div className="w-2 h-2 rounded-full bg-green-400 mr-2" data-id="element-536" />
                  Same Day Delivery
                </div>
                <div className="flex items-center" data-id="element-537">
                  <div className="w-2 h-2 rounded-full bg-rose-400 mr-2" data-id="element-538" />
                  100% Personalized
                </div>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-539">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-square" data-id="element-540">
                <img src="https://cdn.magicpatterns.com/uploads/tY5UdKvSqpLdqk7cARauxk/95b5029e17a0b637aa97170e10c3120c.jpg" alt="Happy Valentine's Day balloon with chocolates and roses" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" data-id="element-541" />

                {/* Floating Card 1 */}
                <motion.div animate={{
                y: [0, -10, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute top-8 left-8 bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg max-w-[160px]" data-id="element-542">
                  <div className="text-xs font-semibold text-gray-500 mb-1" data-id="element-543">
                    Bestseller
                  </div>
                  <div className="font-serif font-bold text-gray-900" data-id="element-544">
                    Valentine's Special
                  </div>
                </motion.div>

                {/* Floating Card 2 */}
                <motion.div animate={{
                y: [0, 10, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1
              }} className="absolute bottom-8 right-8 bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg max-w-[160px]" data-id="element-545">
                  <div className="flex items-center gap-1 mb-1" data-id="element-546">
                    {[1, 2, 3, 4, 5].map(i => <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-current" data-id="element-547" />)}
                  </div>
                  <div className="text-xs font-medium text-gray-600" data-id="element-548">
                    "The perfect gift for my sister's birthday!"
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customization Wizard Modal */}
      <CustomizationWizard isOpen={isCustomizationOpen} onClose={() => setIsCustomizationOpen(false)} data-id="element-549" />
    </>;
}